import { SUPPORTED_FILE_EXTENSIONS } from '@/backend/types'
import type { Field } from '@/modules/Project/Fields/types'
import { useFileUrlUpload } from '@/modules/Project/useFileUrlUpload'
import { useUploadToFileField } from '@/modules/Project/useUploadToFileField'
import { getFilesFromClipboardEvent } from '@/shared/clipboard'
import { toast } from '@/shared/toast'
import { isValidUrl } from '@/shared/utils/string'
import { useFileType } from '@/sharedComposables/useFileType'
import { useRouteParams } from '@/sharedComposables/useRouteParams'
import { computed, ref, watch, type Ref } from 'vue'
import { canPreviewFileField } from './Fields/utils/file'
import { resolveFilename } from './resolveFilename'
import { fileURL } from './useProjectStore'
import { useSupportedMimeTypes } from './useSupportedMimeTypes'

/**
 * Provides functionality that is common to:
 * - a file field as represented by a table cell
 * - a file field as represented by an entity view card
 * - any other places we render file fields
 */
export const useFileField = (field: Ref<Field<'file' | 'file_collection' | 'data'>>) => {
  const filename = computed(() => resolveFilename(field.value))

  const { supportedMimeTypes, filterFilesByMimeTypes } = useSupportedMimeTypes(
    computed(() => field.value.type),
  )
  const supportedFileExtensions = [...SUPPORTED_FILE_EXTENSIONS]

  const displayUrl = computed<string | null>(() => fileURL(field.value, 'display'))
  const downloadUrl = computed<string | null>(() => fileURL(field.value, 'download'))

  const fileType = useFileType(displayUrl)

  /**
   * When there is a file, we either:
   * - Let the user preview the file inline for supported file types
   * - Let the user download the file for unsupported file types
   * This computed prop returns values we can pass to our button component to
   * support either case.
   */
  const previewButton = computed<null | {
    text: string
    attrs: { to?: string; target?: string }
  }>(() => {
    if (canPreviewFileField(field.value)) {
      return { text: 'Preview', attrs: {} }
    }

    if (downloadUrl.value) {
      return {
        attrs: { href: downloadUrl.value, target: '_blank' },
        text: 'Download',
      }
    }

    return null
  })

  const { parentEntityId } = useRouteParams()

  const uploadToFileField = useUploadToFileField(supportedMimeTypes)

  const dialogFiles = ref<File[]>([])
  watch(dialogFiles, (files) => {
    uploadToFileField({
      filesToUpload: files,
      entityId: field.value.entityId,
      parentEntityId: parentEntityId.value || null,
      propertyId: field.value.propertyId,
      workspaceId: field.value.workspaceId,
      projectId: field.value.projectId,
    })
  })

  const { uploadFromUrl } = useFileUrlUpload()
  const onPaste = (e: ClipboardEvent) => {
    const files = getFilesFromClipboardEvent(e)
    if (files) {
      const { supportedFiles, unsupportedMimeTypes } = filterFilesByMimeTypes(files)

      if (unsupportedMimeTypes.length > 0) {
        toast.error(`Unsupported file types: ${unsupportedMimeTypes.join(', ')}`)
      }
      dialogFiles.value = supportedFiles
      e.stopImmediatePropagation()
      e.preventDefault()
    } else {
      // see if the clipboard contains a URL
      const clipboardText = e.clipboardData?.getData('text/plain') || ''
      const isUrl = isValidUrl(clipboardText)
      if (isUrl) {
        uploadFromUrl({ field: field.value, url: clipboardText })
        e.stopImmediatePropagation()
        e.preventDefault()
      }
    }
  }

  return {
    /**
     * This is a URL to a PDF version of the file, that can be previewed in the browser. This
     * is used when the user uploads a file that cannot be previewed in the browser (e.g. .docx).
     * The backend converts the file to a PDF so that we can preview it.
     */
    displayUrl,
    /** This is a signed URL to download the exact file the user uploaded. */
    downloadUrl,
    filename,
    supportedMimeTypes,
    supportedFileExtensions,
    fileType,
    previewButton,
    dialogFiles,
    onPaste,
  }
}
